import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Mail, Plus, Send, Eye, Trash2, Save, FileText, Users, X, ArrowLeft,
  RefreshCw, Clock, CheckCircle2, Image as ImageIcon, Upload, Loader2, Wand2, Megaphone,
  Calendar, Shield, AlertTriangle, Zap,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import SortableTable from '../../components/SortableTable';
import VisualBlockEditor from '../../components/VisualBlockEditor';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';
import ModalPortal from '../../components/ModalPortal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AUDIENCE_OPTIONS = [
  { id: 'all', label: 'All Members', hint: 'Every active account' },
  { id: 'business', label: 'Business Members', hint: 'Companies only' },
  { id: 'individual', label: 'Individual Members', hint: 'Personal memberships' },
  { id: 'leadership', label: 'Leadership', hint: 'Board + committee leads' },
  { id: 'active', label: 'Active Only', hint: 'Activated accounts' },
  { id: 'pending', label: 'Pending / Inactive', hint: 'Invited or unactivated' },
];

const AudiencePill = ({ audience }) => {
  const opt = AUDIENCE_OPTIONS.find((o) => o.id === audience);
  if (!opt) return <span className="text-slate-400">Custom</span>;
  return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-semibold bg-[#002249]/10 text-[#002249] border border-[#002249]/15">{opt.label}</span>;
};

const StatusPill = ({ status }) => {
  if (status === 'sent') return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200"><CheckCircle2 className="w-3 h-3" />Sent</span>;
  if (status === 'scheduled') return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-semibold bg-sky-100 text-sky-800 border border-sky-200"><Clock className="w-3 h-3" />Scheduled</span>;
  return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-semibold bg-amber-100 text-amber-800 border border-amber-200"><FileText className="w-3 h-3" />Draft</span>;
};

const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('en', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';

const NewsletterCenterPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // list | edit
  const [current, setCurrent] = useState(null); // editing doc
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewCount, setPreviewCount] = useState(0);
  const [testEmail, setTestEmail] = useState('');
  const [testSending, setTestSending] = useState(false);
  const [sendingBroadcast, setSendingBroadcast] = useState(false);
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [broadcastForm, setBroadcastForm] = useState({ title: '', body: '', link: '', audience: 'all' });
  const [autogenOpen, setAutogenOpen] = useState(false);
  const [autogenTemplate, setAutogenTemplate] = useState('community');
  const [autogenBusy, setAutogenBusy] = useState(false);
  const [templates, setTemplates] = useState([]);

  // Scheduling
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('09:00');
  const [scheduling, setScheduling] = useState(false);

  // Automation / Failsafe settings
  const [automationOpen, setAutomationOpen] = useState(false);
  const [automation, setAutomation] = useState(null);
  const [automationSaving, setAutomationSaving] = useState(false);

  // Opt-in counts (for Command Bar indicator)
  const [optInCounts, setOptInCounts] = useState({ blog_posts: 0, events: 0, announcements: 0, company_profiles: 0 });

  // Lock body scroll while ANY modal is open
  useBodyScrollLock(scheduleOpen || autogenOpen || broadcastOpen || automationOpen);

  useEffect(() => {
    (async () => {
      try {
        const [tmpl, auto, counts] = await Promise.all([
          axios.get(`${API}/admin/newsletters/templates`, { withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${API}/admin/settings/newsletter-automation`, { withCredentials: true }).catch(() => ({ data: null })),
          axios.get(`${API}/admin/newsletter-opt-in/counts`, { withCredentials: true }).catch(() => ({ data: {} })),
        ]);
        setTemplates(tmpl.data || []);
        setAutomation(auto.data);
        setOptInCounts(counts.data || {});
      } catch { /* */ }
    })();
  }, []);

  const handleAutoGenerate = async () => {
    setAutogenBusy(true);
    try {
      const res = await axios.post(`${API}/admin/newsletters/autogen-monthly`, { template: autogenTemplate }, { withCredentials: true });
      toast.success('Monthly draft generated. Review and customize before sending.');
      setAutogenOpen(false);
      setCurrent(res.data);
      setView('edit');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Auto-generate failed');
    } finally {
      setAutogenBusy(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/newsletters`, { withCredentials: true });
      setItems(res.data || []);
    } catch {
      toast.error('Failed to load newsletters');
    } finally { setLoading(false); }
  };

  const startNew = () => {
    setCurrent({
      id: null,
      subject: '',
      body_html: '',
      banner_url: '',
      audience: 'all',
      recipient_ids: [],
      status: 'draft',
      template: 'community',
      accent_color: '#E63946',
      blocks: [],
    });
    setView('edit');
    setPreviewHtml(null);
  };

  const openExisting = (n) => {
    setCurrent({
      ...n,
      recipient_ids: n.recipient_ids || [],
      blocks: n.blocks || [],
      template: n.template || 'community',
      accent_color: n.accent_color || '#E63946',
    });
    setView('edit');
    setPreviewHtml(null);
  };

  const saveDraft = async () => {
    if (!current) return;
    setSaving(true);
    try {
      const payload = { ...current, status: current.status === 'sent' ? 'sent' : 'draft' };
      const res = await axios.post(`${API}/admin/newsletters`, payload, { withCredentials: true });
      setCurrent(res.data);
      await fetchAll();
      toast.success('Draft saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const buildPreview = async () => {
    if (!current?.subject?.trim() || !current?.body_html?.trim()) {
      toast.error('Subject and body required');
      return;
    }
    // Save first, then preview
    try {
      const saved = await axios.post(`${API}/admin/newsletters`, { ...current, status: 'draft' }, { withCredentials: true });
      const res = await axios.post(`${API}/admin/newsletters/${saved.data.id}/preview`, {}, { withCredentials: true });
      setCurrent({ ...saved.data });
      setPreviewHtml(res.data.html);
      setPreviewCount(res.data.recipient_count);
      await fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Preview failed');
    }
  };

  const sendNow = async () => {
    if (!current?.id) { toast.error('Save the draft first'); return; }
    if (!window.confirm(`Send "${current.subject}" to ${previewCount || '(calculating)'} recipient(s)?`)) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/admin/newsletters/${current.id}/send`, {}, { withCredentials: true });
      toast.success(`Sent to ${res.data.delivered} recipient(s)${res.data.failed ? ` · ${res.data.failed} failed` : ''}`);
      await fetchAll();
      setView('list');
      setCurrent(null);
      setPreviewHtml(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Send failed');
    } finally { setSending(false); }
  };

  const deleteOne = async (id, subject) => {
    if (!window.confirm(`Delete "${subject}"?`)) return;
    try {
      await axios.delete(`${API}/admin/newsletters/${id}`, { withCredentials: true });
      toast.success('Deleted');
      await fetchAll();
    } catch {
      toast.error('Delete failed');
    }
  };

  const testSend = async () => {
    if (!testEmail || !current?.subject || !current?.body_html) {
      toast.error('Fill email, subject, and body first');
      return;
    }
    setTestSending(true);
    try {
      const res = await axios.post(`${API}/admin/newsletters/test-send`, {
        to_email: testEmail,
        subject: current.subject,
        body_html: current.body_html,
        banner_url: current.banner_url || null,
      }, { withCredentials: true });
      toast.success(res.data.fallback_sender ? res.data.note || 'Sent via fallback' : 'Test email sent');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test send failed');
    } finally { setTestSending(false); }
  };

  const sendBroadcast = async () => {
    if (!broadcastForm.title.trim()) { toast.error('Title required'); return; }
    setSendingBroadcast(true);
    try {
      const res = await axios.post(`${API}/admin/notifications/broadcast`, broadcastForm, { withCredentials: true });
      toast.success(`Notification delivered to ${res.data.delivered} member(s)`);
      setBroadcastOpen(false);
      setBroadcastForm({ title: '', body: '', link: '', audience: 'all' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Broadcast failed');
    } finally { setSendingBroadcast(false); }
  };

  // --- Scheduling ---
  const openSchedule = () => {
    // default to tomorrow 9:00 AM
    const d = new Date();
    d.setDate(d.getDate() + 1);
    setScheduleDate(d.toISOString().slice(0, 10));
    setScheduleTime('09:00');
    setScheduleOpen(true);
  };

  const submitSchedule = async () => {
    if (!current?.subject?.trim() || !current?.body_html?.trim()) {
      toast.error('Subject and body required before scheduling'); return;
    }
    if (!scheduleDate || !scheduleTime) { toast.error('Pick a date and time'); return; }
    const scheduledAt = new Date(`${scheduleDate}T${scheduleTime}:00`).toISOString();
    if (new Date(scheduledAt) <= new Date()) { toast.error('Scheduled time must be in the future'); return; }
    setScheduling(true);
    try {
      // Save first so we have an ID
      const saveRes = await axios.post(`${API}/admin/newsletters`, { ...current, status: current.status === 'sent' ? 'sent' : 'draft' }, { withCredentials: true });
      const nid = saveRes.data.id;
      await axios.post(`${API}/admin/newsletters/${nid}/schedule`, { scheduled_at: scheduledAt }, { withCredentials: true });
      toast.success(`Scheduled for ${new Date(scheduledAt).toLocaleString()}`);
      setScheduleOpen(false);
      await fetchAll();
      setView('list');
      setCurrent(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Schedule failed');
    } finally { setScheduling(false); }
  };

  const unschedule = async (nl) => {
    if (!window.confirm(`Cancel the scheduled send of "${nl.subject}"?`)) return;
    try {
      await axios.post(`${API}/admin/newsletters/${nl.id}/unschedule`, {}, { withCredentials: true });
      toast.success('Moved back to draft');
      await fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel schedule');
    }
  };

  const resendOne = async (nl) => {
    if (!window.confirm(`Resend "${nl.subject}" to the current audience?\n\nThis will deliver the email again — including to new members who joined since the last send.`)) return;
    try {
      const res = await axios.post(`${API}/admin/newsletters/${nl.id}/resend`, {}, { withCredentials: true });
      toast.success(`Resent to ${res.data.delivered} recipient(s)${res.data.failed ? ` · ${res.data.failed} failed` : ''}`);
      await fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Resend failed');
    }
  };

  // --- Automation ---
  const saveAutomation = async (patch) => {
    setAutomationSaving(true);
    try {
      const res = await axios.put(`${API}/admin/settings/newsletter-automation`, patch, { withCredentials: true });
      setAutomation(res.data);
      toast.success('Automation settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setAutomationSaving(false); }
  };

  const columns = useMemo(() => [
    { id: 'subject', label: 'Subject', accessor: (r) => r.subject || '', render: (r) => (
        <div className="min-w-[220px]">
          <p className="text-[13px] font-semibold text-[#050505] truncate">{r.subject || '(no subject)'}</p>
          <p className="text-[11px] text-slate-400 truncate" dangerouslySetInnerHTML={{ __html: (r.body_html || '').replace(/<[^>]+>/g, '').slice(0, 80) }} />
        </div>
      )
    },
    { id: 'audience', label: 'Audience', accessor: (r) => r.audience || '', render: (r) => <AudiencePill audience={r.audience} />, width: '140px' },
    { id: 'status', label: 'Status', accessor: (r) => r.status || 'draft', render: (r) => <StatusPill status={r.status} />, width: '90px' },
    { id: 'recipients', label: 'Recipients', accessor: (r) => r.recipient_count || 0, render: (r) => <span className="text-[12.5px] text-slate-600">{r.recipient_count || '—'}</span>, width: '100px' },
    { id: 'updated_at', label: 'Last Edited', accessor: (r) => r.updated_at || '', render: (r) => <span className="text-[12px] text-slate-500">{fmtDate(r.updated_at)}</span>, width: '180px' },
    { id: 'sent_at', label: 'Sent / Scheduled', accessor: (r) => r.sent_at || r.scheduled_at || '', render: (r) => {
        if (r.status === 'sent') return <span className="text-[12px] text-slate-500">{fmtDate(r.sent_at)}</span>;
        if (r.status === 'scheduled') return <span className="text-[12px] text-sky-700 font-semibold inline-flex items-center gap-1"><Clock className="w-3 h-3" />{fmtDate(r.scheduled_at)}</span>;
        return <span className="text-slate-300">—</span>;
      }, width: '190px' },
    { id: 'actions', label: '', sortable: false, render: (r) => (
        <div className="flex justify-end gap-1">
          {r.status === 'scheduled' && (
            <button onClick={(e) => { e.stopPropagation(); unschedule(r); }} className="h-8 px-2.5 text-[11.5px] border border-sky-200 bg-sky-50 rounded-md hover:bg-sky-100 text-sky-700" data-testid={`nl-unschedule-${r.id}`}>
              Cancel
            </button>
          )}
          {r.status === 'sent' && (
            <button onClick={(e) => { e.stopPropagation(); resendOne(r); }} className="h-8 px-2.5 text-[11.5px] border border-emerald-200 bg-emerald-50 rounded-md hover:bg-emerald-100 text-emerald-700 inline-flex items-center gap-1" data-testid={`nl-resend-${r.id}`} title="Send this newsletter again to the current audience">
              <RefreshCw className="w-3 h-3" />Resend
            </button>
          )}
          <button onClick={(e) => { e.stopPropagation(); openExisting(r); }} className="h-8 px-2.5 text-[11.5px] border border-slate-200 rounded-md hover:bg-slate-50 text-slate-700" data-testid={`nl-edit-${r.id}`}>
            {r.status === 'sent' ? 'View' : 'Edit'}
          </button>
          {r.status !== 'sent' && (
            <button onClick={(e) => { e.stopPropagation(); deleteOne(r.id, r.subject); }} className="h-8 w-8 flex items-center justify-center text-red-500 hover:bg-red-50 rounded-md" data-testid={`nl-delete-${r.id}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      ),
      width: '230px', align: 'right'
    },
  ], []);

  if (view === 'edit' && current) {
    const sent = current.status === 'sent';
    const scheduled = current.status === 'scheduled';
    const totalOptIn = (optInCounts.blog_posts || 0) + (optInCounts.events || 0) + (optInCounts.announcements || 0) + (optInCounts.company_profiles || 0);
    return (
      <div className="pb-8 bg-slate-50 min-h-screen" data-testid="newsletter-editor">
        {/* Sticky Command Bar */}
        {!sent && (
          <div className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-sm" data-testid="nl-command-bar">
            <div className="px-4 sm:px-6 lg:px-8 py-2.5 flex items-center gap-2 flex-wrap">
              <button onClick={() => { setView('list'); setCurrent(null); setPreviewHtml(null); }} className="inline-flex items-center gap-1 text-[12.5px] text-slate-500 hover:text-[#002249] pr-2 border-r border-slate-200 mr-1" data-testid="nl-back">
                <ArrowLeft className="w-4 h-4" />Back
              </button>
              <span className="text-[12.5px] font-semibold text-[#002249] mr-1 truncate max-w-[220px]" title={current.subject || 'New Newsletter'}>
                {current.subject?.trim() || 'New Newsletter'}
              </span>
              <StatusPill status={current.status} />
              {totalOptIn > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-semibold bg-[#E63946]/10 text-[#E63946] border border-[#E63946]/20" title="Items flagged for inclusion across blogs, events, announcements, and businesses">
                  <Zap className="w-3 h-3" />{totalOptIn} in pipeline
                </span>
              )}

              <div className="ml-auto flex items-center gap-1.5 flex-wrap">
                <Button onClick={saveDraft} variant="outline" size="sm" disabled={saving} className="gap-1.5 h-9" data-testid="nl-cmd-save">
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}Save
                </Button>
                <Button onClick={buildPreview} variant="outline" size="sm" className="gap-1.5 h-9" data-testid="nl-cmd-preview">
                  <Eye className="w-3.5 h-3.5" />Preview
                </Button>
                {!scheduled && (
                  <Button onClick={openSchedule} variant="outline" size="sm" className="gap-1.5 h-9 border-sky-200 text-sky-700 hover:bg-sky-50" data-testid="nl-cmd-schedule">
                    <Calendar className="w-3.5 h-3.5" />Schedule
                  </Button>
                )}
                {scheduled && (
                  <Button onClick={() => unschedule(current)} variant="outline" size="sm" className="gap-1.5 h-9 border-sky-200 text-sky-700 hover:bg-sky-50" data-testid="nl-cmd-unschedule">
                    <X className="w-3.5 h-3.5" />Cancel Schedule
                  </Button>
                )}
                <Button onClick={sendNow} size="sm" disabled={sending || !current.id} className="gap-1.5 h-9 bg-[#E63946] hover:bg-[#c02a35]" data-testid="nl-cmd-send">
                  {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}Send Now
                </Button>
              </div>
            </div>
            {scheduled && current.scheduled_at && (
              <div className="px-4 sm:px-6 lg:px-8 pb-2 -mt-0.5">
                <div className="inline-flex items-center gap-1.5 text-[11.5px] text-sky-800 bg-sky-50 border border-sky-200 rounded-md px-2 py-1" data-testid="nl-schedule-banner">
                  <Clock className="w-3 h-3" />Scheduled for <strong>{fmtDate(current.scheduled_at)}</strong> · will send automatically
                </div>
              </div>
            )}
          </div>
        )}
        <div className="p-4 sm:p-6 lg:p-8">
        {sent && (
          <button onClick={() => { setView('list'); setCurrent(null); setPreviewHtml(null); }} className="inline-flex items-center gap-1 text-[13px] text-slate-500 hover:text-[#002249] mb-4" data-testid="nl-back">
            <ArrowLeft className="w-4 h-4" />Back to all newsletters
          </button>
        )}
        <div className="space-y-5">
          {/* Meta bar */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold text-[#002249]">{sent ? 'Sent Newsletter' : current.id ? 'Edit Newsletter' : 'New Newsletter'}</h2>
                <p className="text-[12.5px] text-slate-500 mt-0.5">{sent ? `Sent ${fmtDate(current.sent_at)} — ${current.delivered || 0} delivered` : 'Draft — save often, preview before sending.'}</p>
              </div>
              <StatusPill status={current.status} />
            </div>

            <div>
              <label className="block text-[11.5px] font-semibold text-slate-600 mb-1">Subject</label>
              <Input
                value={current.subject}
                onChange={(e) => setCurrent({ ...current, subject: e.target.value })}
                placeholder="Big News from the DCCF"
                className="h-11 text-[15px]"
                disabled={sent}
                data-testid="nl-subject"
              />
            </div>

            {/* Template picker */}
            {!sent && templates.length > 0 && (
              <div>
                <label className="block text-[11.5px] font-semibold text-slate-600 mb-1">Template</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                  {templates.map((t) => {
                    const active = (current.template || 'community') === t.id;
                    return (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => setCurrent({ ...current, template: t.id, accent_color: t.accent })}
                        className={`px-2.5 py-2 rounded-md border text-left transition-colors u-btn ${
                          active ? 'border-[#002249] bg-[#002249]/5' : 'border-slate-200 hover:border-slate-300 bg-white'
                        }`}
                        data-testid={`nl-editor-tmpl-${t.id}`}
                      >
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: t.accent }} />
                          <span className="text-[11.5px] font-bold text-[#002249]">{t.name}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Block summary (if any) */}
            {current.blocks && current.blocks.length > 0 && (
              <div className="border border-slate-200 rounded-lg p-3 bg-slate-50">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[11.5px] font-semibold text-slate-600">Content Blocks ({current.blocks.length})</p>
                  <span className="text-[10.5px] text-slate-400">Edit raw HTML below, or regenerate</span>
                </div>
                <div className="flex flex-wrap gap-1.5" data-testid="nl-block-pills">
                  {current.blocks.map((b, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-semibold bg-white border border-slate-200 text-slate-600 capitalize"
                      data-testid={`nl-block-${b.type}`}
                    >
                      {(b.type || '').replace('_', ' ')}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div>
              <label className="block text-[11.5px] font-semibold text-slate-600 mb-1">Banner image URL <span className="text-slate-400 font-normal">(optional)</span></label>
              <Input
                value={current.banner_url || ''}
                onChange={(e) => setCurrent({ ...current, banner_url: e.target.value })}
                placeholder="https://... (displayed full-width at the top of the email)"
                className="h-10 text-[13px]"
                disabled={sent}
                data-testid="nl-banner"
              />
            </div>

            <div>
              <label className="block text-[11.5px] font-semibold text-slate-600 mb-1">Audience</label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {AUDIENCE_OPTIONS.map((o) => (
                  <button
                    key={o.id}
                    type="button"
                    disabled={sent}
                    onClick={() => setCurrent({ ...current, audience: o.id })}
                    className={`text-left px-3 py-2 rounded-lg border transition-colors ${
                      current.audience === o.id
                        ? 'border-[#002249] bg-[#002249]/5'
                        : 'border-slate-200 hover:border-slate-300'
                    } ${sent ? 'opacity-70' : ''}`}
                    data-testid={`nl-audience-${o.id}`}
                  >
                    <p className="text-[13px] font-semibold text-[#050505]">{o.label}</p>
                    <p className="text-[11px] text-slate-500 leading-tight mt-0.5">{o.hint}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Test send — primary actions now live in the Command Bar above */}
            {!sent && (
              <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-slate-100">
                <div className="flex items-center gap-2 ml-auto">
                  <Input
                    type="email"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="h-9 text-[13px] w-[220px]"
                    data-testid="nl-test-email"
                  />
                  <Button onClick={testSend} disabled={testSending || !testEmail} variant="outline" size="sm" className="gap-1.5 h-9" data-testid="nl-test-send">
                    {testSending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                    Test Send
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Visual block editor — replaces the raw HTML textarea */}
          {!sent && (
            <VisualBlockEditor
              value={current}
              onChange={(next) => setCurrent(next)}
              disabled={sent}
            />
          )}

          {/* Advanced mode — raw HTML */}
          {!sent && (
            <details className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="nl-advanced-mode">
              <summary className="cursor-pointer px-5 py-3 text-[13px] font-semibold text-slate-600 hover:bg-slate-50">
                ⚙️ Advanced — Raw HTML (power users only)
              </summary>
              <div className="px-5 pb-5 pt-2">
                <p className="text-[11.5px] text-slate-500 mb-2">
                  Blocks auto-render into HTML on save. Only edit this if you need fine control — manual edits here are lost if you modify blocks.
                </p>
                <Textarea
                  value={current.body_html || ''}
                  onChange={(e) => setCurrent({ ...current, body_html: e.target.value })}
                  placeholder="<p>Raw HTML body</p>"
                  className="min-h-[200px] font-mono text-[12px]"
                  data-testid="nl-body-raw"
                />
              </div>
            </details>
          )}

          {/* Read-only view for sent newsletters */}
          {sent && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
                <h3 className="text-[13px] font-bold text-[#002249]">Delivered Newsletter</h3>
                <StatusPill status="sent" />
              </div>
              <iframe
                title="sent"
                srcDoc={current.body_html ? `<!doctype html><html><body>${current.body_html}</body></html>` : ''}
                className="w-full h-[500px] bg-white"
                sandbox=""
              />
            </div>
          )}
        </div>
        </div>

        {/* Schedule modal */}
        <AnimatePresence>
          <ModalPortal>
            {scheduleOpen && (
              <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 bg-black/55 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
                onClick={() => setScheduleOpen(false)}
              >
                <motion.div
                  initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
                  transition={{ type: 'spring', damping: 28, stiffness: 260 }}
                  onClick={(e) => e.stopPropagation()}
                  className="bg-white w-full sm:max-w-[460px] sm:rounded-2xl rounded-t-2xl shadow-2xl overflow-hidden"
                  data-testid="nl-schedule-modal"
                >
                  <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
                    <div>
                      <h3 className="text-[15px] font-bold text-[#002249] flex items-center gap-1.5"><Calendar className="w-4 h-4 text-sky-600" />Schedule Newsletter</h3>
                      <p className="text-[11px] text-slate-500 mt-0.5">Pick when this newsletter should auto-send.</p>
                    </div>
                    <button onClick={() => setScheduleOpen(false)} className="p-2 text-slate-500 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
                  </div>
                  <div className="p-5 space-y-3">
                    <div>
                      <label className="block text-[11.5px] font-semibold text-slate-600 mb-1">Date</label>
                      <Input type="date" value={scheduleDate} onChange={(e) => setScheduleDate(e.target.value)} min={new Date().toISOString().slice(0, 10)} className="h-10 text-[14px]" data-testid="nl-schedule-date" />
                    </div>
                    <div>
                      <label className="block text-[11.5px] font-semibold text-slate-600 mb-1">Time</label>
                      <Input type="time" value={scheduleTime} onChange={(e) => setScheduleTime(e.target.value)} className="h-10 text-[14px]" data-testid="nl-schedule-time" />
                    </div>
                    {scheduleDate && scheduleTime && (
                      <p className="text-[11.5px] text-slate-500">
                        Will send on <strong className="text-[#002249]">{new Date(`${scheduleDate}T${scheduleTime}:00`).toLocaleString()}</strong>.
                      </p>
                    )}
                  </div>
                  <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50">
                    <Button variant="outline" onClick={() => setScheduleOpen(false)} disabled={scheduling}>Cancel</Button>
                    <Button
                      onClick={submitSchedule}
                      disabled={scheduling || !scheduleDate || !scheduleTime}
                      className="bg-sky-600 hover:bg-sky-700 gap-1.5"
                      data-testid="nl-schedule-submit"
                    >
                      {scheduling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Clock className="w-3.5 h-3.5" />}
                      Schedule
                    </Button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </ModalPortal>
        </AnimatePresence>
      </div>
    );
  }

  // ========== LIST VIEW ==========
  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-slate-50 min-h-screen" data-testid="newsletter-center-page">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-bold text-[#002249] tracking-tight">Newsletter Center</h1>
          <p className="text-sm text-slate-500 mt-1">
            Compose, preview, and send branded newsletters to DCCF members — plus broadcast in-platform notifications.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchAll} disabled={loading} className="gap-2 h-10" data-testid="nl-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />Refresh
          </Button>
          <Button variant="outline" onClick={() => setAutomationOpen(true)} className="gap-2 h-10 border-slate-200 text-slate-700 hover:bg-slate-50" data-testid="nl-automation-open" title="Weekly failsafe & scheduler settings">
            <Shield className="w-4 h-4" />Automation
            {automation && !automation.failsafe_enabled && <span className="inline-block w-2 h-2 rounded-full bg-amber-400" />}
          </Button>
          <Button variant="outline" onClick={() => setBroadcastOpen(true)} className="gap-2 h-10 border-[#002249]/30 text-[#002249] hover:bg-[#002249]/5" data-testid="nl-broadcast-open">
            <Megaphone className="w-4 h-4" />Broadcast
          </Button>
          <Button variant="outline" onClick={() => setAutogenOpen(true)} className="gap-2 h-10 border-[#E63946]/40 text-[#E63946] hover:bg-[#E63946]/5" data-testid="nl-autogen-open">
            <Wand2 className="w-4 h-4" />Generate Monthly
          </Button>
          <Button onClick={startNew} className="gap-2 h-10 bg-[#E63946] hover:bg-[#c02a35]" data-testid="nl-new">
            <Plus className="w-4 h-4" />New Newsletter
          </Button>
        </div>
      </div>

      {/* Pipeline indicator */}
      {((optInCounts.blog_posts || 0) + (optInCounts.events || 0) + (optInCounts.announcements || 0) + (optInCounts.company_profiles || 0)) > 0 && (
        <div className="mb-4 flex items-center gap-2 px-3 py-2 bg-[#E63946]/5 border border-[#E63946]/15 rounded-lg text-[12.5px] text-[#002249]" data-testid="nl-pipeline-indicator">
          <Zap className="w-3.5 h-3.5 text-[#E63946] flex-shrink-0" />
          <span className="font-semibold">{(optInCounts.blog_posts || 0) + (optInCounts.events || 0) + (optInCounts.announcements || 0) + (optInCounts.company_profiles || 0)} items</span>
          <span className="text-slate-600">flagged for the next newsletter —</span>
          <span className="text-slate-500">{optInCounts.blog_posts || 0} blog{(optInCounts.blog_posts || 0) !== 1 ? 's' : ''} · {optInCounts.events || 0} event{(optInCounts.events || 0) !== 1 ? 's' : ''} · {optInCounts.announcements || 0} announcement{(optInCounts.announcements || 0) !== 1 ? 's' : ''} · {optInCounts.company_profiles || 0} business{(optInCounts.company_profiles || 0) !== 1 ? 'es' : ''}</span>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-white rounded-xl border border-slate-200 px-4 py-3 shadow-sm">
          <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Total</p>
          <p className="text-2xl font-bold text-[#002249] mt-0.5">{items.length}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 px-4 py-3 shadow-sm">
          <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Sent</p>
          <p className="text-2xl font-bold text-emerald-700 mt-0.5">{items.filter((i) => i.status === 'sent').length}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 px-4 py-3 shadow-sm">
          <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Drafts</p>
          <p className="text-2xl font-bold text-amber-600 mt-0.5">{items.filter((i) => i.status !== 'sent').length}</p>
        </div>
      </div>

      <SortableTable
        columns={columns}
        data={items}
        defaultSort={{ id: 'updated_at', direction: 'desc' }}
        onRowClick={openExisting}
        rowTestId={(r) => `nl-row-${r.id}`}
        emptyMessage={loading ? 'Loading…' : 'No newsletters yet — hit "New Newsletter" to compose your first.'}
      />

      {/* Auto-generate monthly modal */}
      <AnimatePresence>
        <ModalPortal>
          {autogenOpen && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black/55 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
              onClick={() => setAutogenOpen(false)}
            >
              <motion.div
                initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
                transition={{ type: 'spring', damping: 28, stiffness: 260 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white w-full sm:max-w-[640px] sm:rounded-2xl rounded-t-2xl shadow-2xl overflow-hidden"
                data-testid="nl-autogen-modal"
              >
                <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
                  <div>
                    <h3 className="text-[15px] font-bold text-[#002249] flex items-center gap-1.5"><Wand2 className="w-4 h-4 text-[#E63946]" />Generate Monthly Newsletter</h3>
                    <p className="text-[11px] text-slate-500">Auto-builds a draft using this month's platform content. You can edit everything before sending.</p>
                  </div>
                  <button onClick={() => setAutogenOpen(false)} className="p-2 text-slate-500 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
                </div>
                <div className="p-5">
                  <label className="block text-[11.5px] font-semibold text-slate-600 mb-2">Choose a template</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    {templates.map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => setAutogenTemplate(t.id)}
                        className={`text-left p-3.5 rounded-lg border-2 transition-all u-btn ${
                          autogenTemplate === t.id
                            ? 'border-[#002249] bg-[#002249]/5 ring-2 ring-[#002249]/10'
                            : 'border-slate-200 hover:border-slate-300 bg-white'
                        }`}
                        data-testid={`nl-tmpl-${t.id}`}
                      >
                        <div className="flex items-center gap-2 mb-1.5">
                          <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: t.accent }} />
                          <span className="text-[13px] font-bold text-[#002249]">{t.name}</span>
                        </div>
                        <p className="text-[11.5px] text-slate-500 leading-snug">{t.description}</p>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50">
                  <Button variant="outline" onClick={() => setAutogenOpen(false)} disabled={autogenBusy}>Cancel</Button>
                  <Button
                    onClick={handleAutoGenerate}
                    disabled={autogenBusy || !autogenTemplate}
                    className="bg-[#E63946] hover:bg-[#c02a35] gap-1.5"
                    data-testid="nl-autogen-submit"
                  >
                    {autogenBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                    Generate Draft
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </ModalPortal>
      </AnimatePresence>

      {/* Broadcast in-platform notification modal */}
      <AnimatePresence>
        <ModalPortal>
          {broadcastOpen && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black/55 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
              onClick={() => setBroadcastOpen(false)}
            >
              <motion.div
                initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
                transition={{ type: 'spring', damping: 28, stiffness: 260 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white w-full sm:max-w-[520px] sm:rounded-2xl rounded-t-2xl shadow-2xl overflow-hidden"
                data-testid="nl-broadcast-modal"
              >
                <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
                  <div>
                    <h3 className="text-[15px] font-bold text-[#002249]">Broadcast Notification</h3>
                    <p className="text-[11px] text-slate-500">Appears in-platform only (no email).</p>
                  </div>
                  <button onClick={() => setBroadcastOpen(false)} className="p-2 text-slate-500 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
                </div>
                <div className="p-5 space-y-3">
                  <div>
                    <label className="block text-[11.5px] font-semibold text-slate-600 mb-1">Title</label>
                    <Input value={broadcastForm.title} onChange={(e) => setBroadcastForm({ ...broadcastForm, title: e.target.value })} placeholder="New event just launched" data-testid="nl-broadcast-title" />
                  </div>
                  <div>
                    <label className="block text-[11.5px] font-semibold text-slate-600 mb-1">Message</label>
                    <Textarea value={broadcastForm.body} onChange={(e) => setBroadcastForm({ ...broadcastForm, body: e.target.value })} placeholder="Short description" className="min-h-[80px]" data-testid="nl-broadcast-body" />
                  </div>
                  <div>
                    <label className="block text-[11.5px] font-semibold text-slate-600 mb-1">Where should this notification take users?</label>
                    <div className="flex flex-wrap gap-1.5 mb-2" data-testid="nl-broadcast-link-presets">
                      {[
                        { id: 'notifications', label: 'Notifications', url: '/notifications' },
                        { id: 'events', label: 'Events', url: '/events' },
                        { id: 'news', label: 'News', url: '/news' },
                        { id: 'membership', label: 'Membership', url: '/membership' },
                        { id: 'leadership', label: 'Leadership', url: '/leadership' },
                        { id: 'portal', label: 'Member Portal', url: '/portal' },
                        { id: 'business', label: 'Business Directory', url: '/members' },
                        { id: 'contact', label: 'Contact', url: '/contact' },
                      ].map((p) => {
                        const active = broadcastForm.link === p.url;
                        return (
                          <button
                            key={p.id}
                            type="button"
                            onClick={() => setBroadcastForm({ ...broadcastForm, link: p.url })}
                            className={`h-7 px-2.5 text-[11px] font-semibold rounded-full border transition-all ${
                              active ? 'bg-[#002249] text-white border-[#002249]' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
                            }`}
                            data-testid={`nl-broadcast-link-preset-${p.id}`}
                          >
                            {p.label}
                          </button>
                        );
                      })}
                    </div>
                    <Input
                      value={broadcastForm.link}
                      onChange={(e) => setBroadcastForm({ ...broadcastForm, link: e.target.value })}
                      placeholder="/portal · /events/123 · or full https://… URL"
                      data-testid="nl-broadcast-link"
                    />
                    <p className="mt-1 text-[10.5px] text-slate-400">Leave blank to land users on their notifications page.</p>
                  </div>
                  <div>
                    <label className="block text-[11.5px] font-semibold text-slate-600 mb-1">Audience</label>
                    <select value={broadcastForm.audience} onChange={(e) => setBroadcastForm({ ...broadcastForm, audience: e.target.value })} className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-[14px]" data-testid="nl-broadcast-audience">
                      {AUDIENCE_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                    </select>
                  </div>
                </div>
                <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50">
                  <Button variant="outline" onClick={() => setBroadcastOpen(false)}>Cancel</Button>
                  <Button onClick={sendBroadcast} disabled={sendingBroadcast} className="bg-[#E63946] hover:bg-[#c02a35] gap-1.5" data-testid="nl-broadcast-send">
                    {sendingBroadcast && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                    Broadcast
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </ModalPortal>
      </AnimatePresence>

      {/* Automation / Failsafe settings modal */}
      <AnimatePresence>
        <ModalPortal>
          {automationOpen && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black/55 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
              onClick={() => setAutomationOpen(false)}
            >
              <motion.div
                initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
                transition={{ type: 'spring', damping: 28, stiffness: 260 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white w-full sm:max-w-[560px] sm:rounded-2xl rounded-t-2xl shadow-2xl overflow-hidden"
                data-testid="nl-automation-modal"
              >
                <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
                  <div>
                    <h3 className="text-[15px] font-bold text-[#002249] flex items-center gap-1.5"><Shield className="w-4 h-4 text-[#002249]" />Newsletter Automation</h3>
                    <p className="text-[11px] text-slate-500 mt-0.5">Scheduler + weekly failsafe — your communication engine never sleeps.</p>
                  </div>
                  <button onClick={() => setAutomationOpen(false)} className="p-2 text-slate-500 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
                </div>
                {automation ? (
                  <div className="p-5 space-y-4">
                    {/* Scheduler toggle */}
                    <div className="flex items-center justify-between gap-4 p-3 bg-slate-50 border border-slate-200 rounded-lg">
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-bold text-[#002249] flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-sky-600" />Scheduler</p>
                        <p className="text-[11.5px] text-slate-500">Fires scheduled newsletters at their exact send time.</p>
                      </div>
                      <button
                        onClick={() => saveAutomation({ scheduler_enabled: !automation.scheduler_enabled })}
                        disabled={automationSaving}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${automation.scheduler_enabled ? 'bg-emerald-500' : 'bg-slate-300'}`}
                        data-testid="nl-scheduler-toggle"
                      >
                        <span className={`inline-block w-4 h-4 rounded-full bg-white transform transition-transform ${automation.scheduler_enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                      </button>
                    </div>

                    {/* Failsafe toggle */}
                    <div className="p-3 bg-[#E63946]/5 border border-[#E63946]/20 rounded-lg space-y-3">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <p className="text-[13px] font-bold text-[#002249] flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5 text-[#E63946]" />Weekly Failsafe</p>
                          <p className="text-[11.5px] text-slate-600">If no newsletter has been sent in the window below, DCCF will auto-generate and send one.</p>
                        </div>
                        <button
                          onClick={() => saveAutomation({ failsafe_enabled: !automation.failsafe_enabled })}
                          disabled={automationSaving}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${automation.failsafe_enabled ? 'bg-[#E63946]' : 'bg-slate-300'}`}
                          data-testid="nl-failsafe-toggle"
                        >
                          <span className={`inline-block w-4 h-4 rounded-full bg-white transform transition-transform ${automation.failsafe_enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                      </div>

                      {automation.failsafe_enabled && (
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-[11px] font-semibold text-slate-600 mb-1">Silence window (days)</label>
                            <Input
                              type="number" min="1" max="60"
                              value={automation.failsafe_days}
                              onChange={(e) => setAutomation({ ...automation, failsafe_days: parseInt(e.target.value) || 7 })}
                              onBlur={() => saveAutomation({ failsafe_days: automation.failsafe_days })}
                              className="h-9 text-[13px]"
                              data-testid="nl-failsafe-days"
                            />
                          </div>
                          <div>
                            <label className="block text-[11px] font-semibold text-slate-600 mb-1">Template</label>
                            <select
                              value={automation.failsafe_template}
                              onChange={(e) => { setAutomation({ ...automation, failsafe_template: e.target.value }); saveAutomation({ failsafe_template: e.target.value }); }}
                              className="w-full h-9 px-2 rounded-md border border-slate-200 bg-white text-[13px]"
                              data-testid="nl-failsafe-template"
                            >
                              {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                            </select>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Status */}
                    <div className="text-[11.5px] text-slate-500 space-y-0.5 px-1">
                      <p>Last failsafe check: <span className="text-slate-700">{automation.last_failsafe_check ? fmtDate(automation.last_failsafe_check) : 'never'}</span></p>
                      <p>Last failsafe send: <span className="text-slate-700">{automation.last_failsafe_sent_at ? fmtDate(automation.last_failsafe_sent_at) : 'never'}</span></p>
                    </div>
                  </div>
                ) : (
                  <div className="p-10 flex items-center justify-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
                )}
                <div className="flex justify-end px-5 py-3 border-t border-slate-200 bg-slate-50">
                  <Button variant="outline" onClick={() => setAutomationOpen(false)}>Close</Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </ModalPortal>
      </AnimatePresence>
    </div>
  );
};

export default NewsletterCenterPage;
